// Every tile in the app is a 9:16 vertical poster. Until real artwork exists, the poster is
// drawn from the show's colour and title; the real app swaps in a 9:16 WebP with a blurhash.
import { prefetchShow } from '../lib/prefetch';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from '../lib/router';
import { useCatalog } from '../lib/catalog';
import type { Show } from '../lib/types';

export function Badge({ kind }: { kind: 'plus' | 'free' | 'new' }) {
  if (kind === 'new') return <span className="badge badge-new"><span className="badge-dot" aria-hidden="true" />NEW</span>;
  return <span className={`badge badge-${kind}`}>{kind.toUpperCase()}</span>;
}

export function PosterArt({ show, size = 'md', children, progress, rank }: {
  show: Show;
  size?: 'sm' | 'md' | 'lg' | 'fill';
  children?: ReactNode;
  progress?: number;
  rank?: number;
}) {
  const { topics: TOPICS } = useCatalog();
  const style = { '--poster': show.color } as CSSProperties;
  const topic = TOPICS.find((t) => t.slug === show.topic);
  return (
    <span className={`poster poster-${size}`} style={style}>
      <span className="poster-badges">
        {show.isNew && <Badge kind="new" />}
        {show.access === 'plus' ? <Badge kind="plus" /> : <Badge kind="free" />}
      </span>
      {show.posterUrl
        ? <img className="poster-img" src={show.posterUrl} alt="" loading="lazy" decoding="async" draggable={false} />
        : <><span className="poster-glyph" aria-hidden="true">{topic?.glyph}</span><span className="poster-title">{show.title}</span></>}
      {children}
      {typeof progress === 'number' && (
        <span className="poster-progress"><span style={{ width: `${Math.min(100, progress * 100)}%` }} /></span>
      )}
      {rank !== undefined && <span className="sr-only">Rank {rank}</span>}
    </span>
  );
}

export function PosterLink({ show, size = 'md', meta }: { show: Show; size?: 'sm' | 'md' | 'lg' | 'fill'; meta?: string }) {
  return (
    <Link
      to={`/show/${show.slug}`}
      className="poster-link"
      transition
      // Start loading the show the moment a finger or mouse goes down (≈100 ms before the click).
      onPointerDown={() => prefetchShow(show.slug)}
      onClick={(e) => {
        // Only the tapped poster gets the shared name, so it morphs into the show header.
        document.querySelectorAll<HTMLElement>('.poster[style*="view-transition-name"]').forEach((el) => { el.style.viewTransitionName = ''; });
        const art = e.currentTarget.querySelector<HTMLElement>('.poster');
        if (art) art.style.viewTransitionName = 'show-poster';
      }}
    >
      <PosterArt show={show} size={size} />
      {meta && <span className="poster-meta">{meta}</span>}
    </Link>
  );
}
